import React, { useState, useEffect } from 'react'
import Modal from 'src/components/Modal'
import ModalBody from 'src/components/Modal/ModalBody'
import ModalFooter from 'src/components/Modal/ModalFooter'
import ModalHeader from 'src/components/Modal/ModalHeader'
import TwinButton from 'src/components/TwinButton'
import { numberWithCommas } from 'src/utils/thousandSeprator'
import { RxExternalLink, RxPlus, RxTrash } from "react-icons/rx";
import Select from 'react-select'
import { get } from 'src/services/HttpClient'
import { notify } from 'src/services/Notification/Notification'
import { put } from 'src/services/HttpClient/HttpClient'

export default function UpdateOrderModal({ rowData, isModalOpen, setIsModalOpen, setOrders, orders }) {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [editedItems, setEditedItems] = useState([])
    const [newItems, setNewItems] = useState([])
    const [productsList, setProductsList] = useState([])
    const [selectedProduct, setSelectedProduct] = useState(null)
    const [isProductsLoading, setIsProductsLoading] = useState(false)

    // گرفتن لیست محصولات
    useEffect(() => {
        if (isEditModalOpen && productsList.length === 0) {
            fetchProducts()
        }
    }, [isEditModalOpen])

    const fetchProducts = async () => {
        setIsProductsLoading(true)
        try {
            const result = await get('/products?limit=1000&status=publish')
            if (result.statusCode === 200) {
                const formattedProducts = result.data.map(product => ({
                    value: product.id,
                    label: `${product.name} (${product.sku}) - موجودی: ${product.stockQuantity}`,
                    product: product,
                    stockQuantity: product.stockQuantity
                }))
                setProductsList(formattedProducts.filter(product => product.stockQuantity))
            }
        } catch (error) {
            console.error("خطا در دریافت محصولات:", error)
            notify("خطا در دریافت لیست محصولات", "error")
        } finally {
            setIsProductsLoading(false)
        }
    }

    // وقتی مودال اصلی باز میشه، دیتا رو آماده کن
    useEffect(() => {
        if (isModalOpen && rowData?.orderItems) {
            setEditedItems(rowData.orderItems.map(item => ({ ...item, isNew: false, markedForDelete: false })))
            setNewItems([])
        }
    }, [isModalOpen, rowData])

    // باز کردن مودال ویرایش و بستن مودال اصلی
    const openEditModal = () => {
        setEditedItems(rowData.orderItems.map(item => ({ ...item, isNew: false, markedForDelete: false })))
        setNewItems([])
        setSelectedProduct(null)
        setIsModalOpen(false)
        setTimeout(() => {
            setIsEditModalOpen(true)
        }, 200)
    }

    // بازگشت به مودال اصلی بعد از بستن مودال ویرایش
    const closeEditModal = () => {
        setIsEditModalOpen(false)
        setTimeout(() => {
            setIsModalOpen(true)
        }, 200)
    }

    // تغییر تعداد محصول موجود
    const handleQuantityChange = (index, newQuantity, isNewItem = false) => {
        if (newQuantity < 0) return

        if (isNewItem) {
            const updatedNewItems = [...newItems]
            updatedNewItems[index].quantity = newQuantity
            setNewItems(updatedNewItems)
        } else {
            const updatedItems = [...editedItems]
            updatedItems[index].quantity = newQuantity
            setEditedItems(updatedItems)
        }
    }

    // حذف/بازگردانی محصول از سفارش
    const handleRemoveItem = (index, isNewItem = false) => {
        if (isNewItem) {
            setNewItems(newItems.filter((_, i) => i !== index))
        } else {
            const updatedItems = [...editedItems]
            updatedItems[index].markedForDelete = !updatedItems[index].markedForDelete
            setEditedItems(updatedItems)
        }
    }

    // اضافه کردن محصول جدید به سفارش
    const handleAddProduct = () => {
        if (!selectedProduct) {
            notify("لطفاً یک محصول انتخاب کنید", "warning")
            return
        }

        const product = selectedProduct.product
        const existingInEdited = editedItems.find(item => item.productId === product.id && !item.markedForDelete)
        const existingInNew = newItems.find(item => item.productId === product.id)

        if (existingInEdited || existingInNew) {
            notify("این محصول قبلاً به سفارش اضافه شده است", "warning")
            return
        }

        const newOrderItem = {
            id: `temp_${Date.now()}_${Math.random()}`,
            productId: product.id,
            title: product.name,
            sku: product.sku,
            quantity: 1,
            price: Number(product.price),
            salePrice: Number(product.salePrice || product.price),
            featuredImage: product.featuredImage,
            product: product,
            isNew: true,
            temp: true
        }

        setNewItems([...newItems, newOrderItem])
        setSelectedProduct(null)
    }

    // محاسبه مجموع مبالغ کالاها
    const calculateSubtotal = () => {
        const existingTotal = editedItems
            .filter(item => !item.markedForDelete)
            .reduce((sum, item) => sum + (Number(item.salePrice) * item.quantity), 0)

        const newTotal = newItems.reduce((sum, item) => sum + (Number(item.salePrice) * item.quantity), 0)

        return existingTotal + newTotal
    }

    // محاسبه جمع کل با احتساب هزینه ارسال
    // محاسبه جمع کل با احتساب تخفیف داینامیک و هزینه ارسال
    const calculateTotals = () => {

        const subtotal = calculateSubtotal()

        // هزینه ارسال
        const shippingCost = rowData?.shipping?.[0]?.cost
            ? Number(rowData.shipping[0].cost)
            : 0

        // مبلغ کل اولیه سفارش
        const originalItemsTotal = Number(rowData?.itemsTotal || 0)

        // تخفیف اولیه سفارش
        const originalDiscountTotal = Number(rowData?.discountTotal || 0)

        // درصد تخفیف
        const discountPercent =
            originalItemsTotal > 0
                ? originalDiscountTotal / originalItemsTotal
                : 0
        
        // تخفیف جدید براساس مبلغ فعلی
        const discountTotal = Math.round(subtotal * discountPercent)

        // جمع نهایی
        const total = subtotal + shippingCost - discountTotal

        return {
            subtotal,
            shippingCost,
            discountTotal,
            total: total > 0 ? total : 0
        }
    }

    // آماده سازی دیتا برای ارسال به سرور
    const prepareDataForSubmit = () => {
        const updatedItems = []
        const deletedItems = []
        const addedItems = []

        editedItems.forEach(item => {
            const originalItem = rowData.orderItems.find(oi => oi.id === item.id)

            if (item.markedForDelete) {
                deletedItems.push({ orderItemId: item.id })
            } else if (originalItem && originalItem.quantity !== item.quantity) {
                updatedItems.push({
                    orderItemId: item.id,
                    quantity: item.quantity
                })
            }
        })

        newItems.forEach(item => {
            addedItems.push({
                productId: item.productId,
                quantity: item.quantity,
                price: Number(item.price),
                salePrice: Number(item.salePrice)
            })
        })

        return { updatedItems, deletedItems, addedItems }
    }

    const handleSaveChanges = async () => {
        const { updatedItems, deletedItems, addedItems } = prepareDataForSubmit()

        if (updatedItems.length === 0 && deletedItems.length === 0 && addedItems.length === 0) {
            notify("هیچ تغییری اعمال نشده است")
            return
        }

        if(rowData.paymentMethod==="snapppay"){
            const confirmed = window.confirm("تغییر وضعیت سفارشی که با اسنپ پی پرداخت شده غیر قابل بازگشت است، آیا از انجام این عملیات اطمینان دارید؟ ");
            if (!confirmed) return;
        }

        setIsLoading(true)

        try {
            let result = await put(`/orders/${rowData?.id}`, {
                updateItems: updatedItems,
                deleteItems: deletedItems,
                addItems: addedItems
            })

            if (result.statusCode === 200) {
                notify("تغییرات سفارش با موفقیت ذخیره شد")

                if (setOrders && orders) {
                    const updatedOrder = result.data
                    setOrders(orders.map(order =>
                        order.id === rowData.id ? updatedOrder : order
                    ))
                }

                setIsEditModalOpen(false)
                setNewItems([])

                setTimeout(() => {
                    setIsModalOpen(true)
                }, 200)
            } else {
                notify(result.message || "خطایی در ذخیره تغییرات رخ داده است", "error")
            }
        } catch (error) {
            console.error("خطا در ذخیره:", error)
            notify("خطایی در ارتباط با سرور رخ داده است", "error")
        } finally {
            setIsLoading(false)
        }
    }

    const handleCancel = () => {
        setIsEditModalOpen(false)
        setEditedItems([])
        setNewItems([])
        setSelectedProduct(null)
        setTimeout(() => {
            setIsModalOpen(true)
        }, 200)
    }

    const totals = calculateTotals()

    const subtotal = totals.subtotal
    const shippingCost = totals.shippingCost
    const discountTotal = totals.discountTotal
    const finalTotal = totals.total

    return (
        <>
            {/* مودال نمایش جزئیات (غیرفعال) */}
            <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(false)} size="xl">
                <ModalHeader>
                    <div className='flex justify-between w-full items-center'>
                        <span>
                            جزئیات سفارش #{rowData?.orderNumber}
                        </span>
                        <button
                            className='bg-primary-color text-white px-3 py-2 rounded-xl text-sm'
                            onClick={openEditModal}
                        >
                            ویرایش سفارش
                        </button>
                    </div>
                </ModalHeader>

                <ModalBody>
                    <div className="w-full flex flex-col gap-1 p-4">
                        {/* اطلاعات مشتری */}
                        <div className="flex flex-col gap-1 lg:flex-row lg:gap-10 mb-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div className="flex gap-1">
                                <span className="font-semibold">تحویل گیرنده:</span>
                                <span>{rowData?.receiverName}</span>
                            </div>
                            <div className="flex gap-1">
                                <span className="font-semibold">تلفن:</span>
                                <span>{rowData?.receiverPhone}</span>
                            </div>
                            <div className="flex gap-1">
                                <span className="font-semibold">استان و شهر:</span>
                                <span>{rowData?.province} - {rowData?.city}</span>
                            </div>
                        </div>

                        {/* لیست محصولات سفارش (فقط نمایش) */}
                        <div className="space-y-2">
                            <h3 className="font-semibold">محصولات سفارش</h3>
                            {rowData?.orderItems?.map((item) => (
                                <div key={item.id} className="p-3 flex flex-col lg:flex-row items-center justify-between gap-2 w-full border border-gray-200 rounded-xl">
                                    <div className="flex flex-col lg:flex-row gap-3 items-center flex-1">
                                        <img
                                            className="w-14 h-14 object-cover border border-gray-100 rounded-xl"
                                            src={`${process.env.REACT_APP_CDN_URL}/thumbnail/${item?.productId}/${item?.product?.featuredImage}`}
                                            alt={item?.title}
                                        />
                                        <div className="flex gap-2 items-center">
                                            <span className="text-sm font-medium">{item?.title}</span>
                                            <span className="text-xs text-gray-500">({item?.sku})</span>
                                        </div>
                                        <div className="flex gap-2 items-center">
                                            <div className="flex gap-1 items-center bg-gray-100 px-2 py-1 rounded-lg text-xs">
                                                <span>{item.quantity}</span>
                                                <span>عدد</span>
                                            </div>
                                            <a target="_blank" rel="noopener noreferrer" href={`${process.env.REACT_APP_SITE_URL}?p=${item.productId}`}>
                                                <RxExternalLink size={18} color="blue" />
                                            </a>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-1">
                                            <span className="line-through text-gray-400 text-sm">
                                                {numberWithCommas(Number(item.price) * item.quantity)}
                                            </span>
                                            <span className="font-semibold">
                                                {numberWithCommas(Number(item.salePrice) * item.quantity)}
                                            </span>
                                            <span className="text-xs">تومان</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* بخش جمع کل در مودال نمایش */}
                        <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                            <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <span>مجموع کالاها:</span>
                                    <span>{numberWithCommas(Number(rowData?.itemsTotal) || 0)} تومان</span>
                                </div>
                                {Number(rowData?.discountTotal) > 0 && (
                                    <div className="flex justify-between items-center text-green-600">
                                        <span>تخفیف:</span>
                                        <span>- {numberWithCommas(Number(rowData.discountTotal))} تومان</span>
                                    </div>
                                )}
                                <div className="flex justify-between items-center">
                                    <span>هزینه ارسال:</span>
                                    <span>
                                        {Number(rowData?.shipping?.[0]?.cost) > 0
                                            ? `${numberWithCommas(Number(rowData.shipping[0].cost))} تومان`
                                            : 'رایگان'}
                                    </span>
                                </div>
                                <div className="pt-2 border-t border-gray-300 dark:border-gray-600">
                                    <div className="flex justify-between items-center font-bold">
                                        <span>جمع کل:</span>
                                        <span className="text-primary-color">{numberWithCommas(Number(rowData?.totalAmount))} تومان</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </ModalBody>

                <ModalFooter>
                    <TwinButton
                        acceptTitle={false}
                        rejectHandle={() => setIsModalOpen(false)}
                        rejectTitle="بستن"
                        isCompact={true}
                    />
                </ModalFooter>
            </Modal>

            {/* مودال ویرایش سفارش */}
            <Modal isOpen={isEditModalOpen} toggle={closeEditModal} size="xl">
                <ModalHeader>
                    <div className='flex justify-between w-full items-center'>
                        <span>
                            ویرایش سفارش #{rowData?.orderNumber}
                        </span>
                    </div>
                </ModalHeader>

                <ModalBody>
                    <div className="w-full flex flex-col gap-1 p-4">
                        {/* اطلاعات مشتری */}
                        <div className="flex flex-col gap-1 lg:flex-row lg:gap-10 mb-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div className="flex gap-1">
                                <span className="font-semibold">تحویل گیرنده:</span>
                                <span>{rowData?.receiverName}</span>
                            </div>
                            <div className="flex gap-1">
                                <span className="font-semibold">تلفن:</span>
                                <span>{rowData?.receiverPhone}</span>
                            </div>
                            <div className="flex gap-1">
                                <span className="font-semibold">استان و شهر:</span>
                                <span>{rowData?.province} - {rowData?.city}</span>
                            </div>
                        </div>

                        {/* فرم اضافه کردن محصول جدید */}
                        <div className="mb-4 p-4 border-2 border-dashed border-primary-color rounded-lg bg-primary-color/5">
                            <h3 className="font-semibold mb-3">اضافه کردن محصول جدید به سفارش</h3>
                            <div className="flex flex-col lg:flex-row gap-3 items-start">
                                <div className="w-full lg:flex-1">
                                    <Select
                                        options={productsList}
                                        value={selectedProduct}
                                        onChange={setSelectedProduct}
                                        isLoading={isProductsLoading}
                                        placeholder="جستجوی محصول..."
                                        isClearable
                                        className="react-select-container w-full"
                                        classNamePrefix="react-select"
                                        dir="rtl"
                                    />
                                </div>
                                <button
                                    onClick={handleAddProduct}
                                    className="bg-primary-color text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-color-dark transition"
                                >
                                    <RxPlus size={20} />
                                    اضافه کن
                                </button>
                            </div>
                        </div>

                        {/* لیست محصولات سفارش (قابل ویرایش) */}
                        <div className="space-y-2">
                            <h3 className="font-semibold">محصولات سفارش</h3>

                            {/* محصولات موجود */}
                            {editedItems.map((item, idx) => {
                                const originalItem = rowData?.orderItems?.find(oi => oi.id === item.id)
                                const hasChanged = originalItem?.quantity !== item.quantity
                                const isDeleted = item.markedForDelete

                                return (
                                    <div key={item.id} className={`p-3 flex flex-col lg:flex-row items-center justify-between gap-2 w-full border rounded-xl transition-all duration-300 ${isDeleted
                                        ? 'border-red-300 bg-red-50 dark:bg-red-900/20 opacity-60'
                                        : hasChanged
                                            ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
                                            : 'border-gray-200'
                                        }`}>
                                        <div className="flex flex-col lg:flex-row gap-3 items-center flex-1">
                                            <img
                                                className="w-14 h-14 object-cover border border-gray-100 rounded-xl"
                                                src={`${process.env.REACT_APP_CDN_URL}/thumbnail/${item?.productId}/${item?.product?.featuredImage}`}
                                                alt={item?.title}
                                            />
                                            <div className="flex gap-2 items-center">
                                                <span className="text-sm font-medium">{item?.title}</span>
                                                <span className="text-xs text-gray-500">({item?.sku})</span>
                                            </div>
                                            <div className="flex gap-2 items-center">
                                                {!isDeleted ? (
                                                    <>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handleQuantityChange(idx, item.quantity - 1, false)}
                                                                className="bg-red-500 text-white w-8 h-8 rounded-lg hover:bg-red-600 transition disabled:opacity-50"
                                                                disabled={item.quantity <= 1}
                                                            >
                                                                -
                                                            </button>
                                                            <span className="w-12 text-center font-semibold">{item.quantity}</span>
                                                            <button
                                                                onClick={() => handleQuantityChange(idx, item.quantity + 1, false)}
                                                                className="bg-green-500 text-white w-8 h-8 rounded-lg hover:bg-green-600 transition"
                                                            >
                                                                +
                                                            </button>
                                                            <span className="text-xs mr-1">عدد</span>
                                                        </div>
                                                        <a target="_blank" rel="noopener noreferrer" href={`${process.env.REACT_APP_SITE_URL}?p=${item.productId}`}>
                                                            <RxExternalLink size={18} color="blue" />
                                                        </a>
                                                    </>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-red-600 font-semibold">حذف شده</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {!isDeleted ? (
                                                <>
                                                    <div className="flex items-center gap-1">
                                                        <span className="line-through text-gray-400 text-sm">
                                                            {numberWithCommas(Number(item.price) * item.quantity)}
                                                        </span>
                                                        <span className="font-semibold">
                                                            {numberWithCommas(Number(item.salePrice) * item.quantity)}
                                                        </span>
                                                        <span className="text-xs">تومان</span>
                                                    </div>
                                                    <button
                                                        onClick={() => handleRemoveItem(idx, false)}
                                                        className="text-red-500 hover:text-red-700 transition p-1"
                                                        title="حذف از سفارش"
                                                    >
                                                        <RxTrash size={20} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-gray-400 line-through">
                                                            {numberWithCommas(Number(item.salePrice) * item.quantity)} تومان
                                                        </span>
                                                    </div>
                                                    <button
                                                        onClick={() => handleRemoveItem(idx, false)}
                                                        className="text-green-500 hover:text-green-700 transition p-1"
                                                        title="بازگردانی به سفارش"
                                                    >
                                                        <RxPlus size={20} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}

                            {/* محصولات جدید اضافه شده */}
                            {newItems.map((item, idx) => (
                                <div key={item.id} className="p-3 flex flex-col lg:flex-row items-center justify-between gap-2 w-full border border-green-400 bg-green-50 dark:bg-green-900/20 rounded-xl">
                                    <div className="flex flex-col lg:flex-row gap-3 items-center flex-1">
                                        <img
                                            className="w-14 h-14 object-cover border border-gray-100 rounded-xl"
                                            src={`${process.env.REACT_APP_CDN_URL}/thumbnail/${item.productId}/${item.featuredImage}`}
                                            alt={item.title}
                                        />
                                        <div className="flex gap-2 items-center">
                                            <span className="text-sm font-medium">{item.title}</span>
                                            <span className="text-xs text-gray-500">({item.sku})</span>
                                        </div>
                                        <div className="flex gap-2 items-center">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleQuantityChange(idx, item.quantity - 1, true)}
                                                    className="bg-red-500 text-white w-8 h-8 rounded-lg hover:bg-red-600 transition"
                                                    disabled={item.quantity <= 1}
                                                >
                                                    -
                                                </button>
                                                <span className="w-12 text-center font-semibold">{item.quantity}</span>
                                                <button
                                                    onClick={() => handleQuantityChange(idx, item.quantity + 1, true)}
                                                    className="bg-green-500 text-white w-8 h-8 rounded-lg hover:bg-green-600 transition"
                                                >
                                                    +
                                                </button>
                                                <span className="text-xs mr-1">عدد</span>
                                            </div>
                                            <a target="_blank" rel="noopener noreferrer" href={`${process.env.REACT_APP_SITE_URL}?p=${item.productId}`}>
                                                <RxExternalLink size={18} color="blue" />
                                            </a>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-1">
                                            <span className="line-through text-gray-400 text-sm">
                                                {numberWithCommas(item.price * item.quantity)}
                                            </span>
                                            <span className="font-semibold text-green-600">
                                                {numberWithCommas(item.salePrice * item.quantity)}
                                            </span>
                                            <span className="text-xs">تومان</span>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveItem(idx, true)}
                                            className="text-red-500 hover:text-red-700 transition p-1"
                                        >
                                            <RxTrash size={20} />
                                        </button>
                                    </div>
                                </div>
                            ))}

                            {/* جمع کل با تفکیک مبالغ */}
                            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">

                                <div className="space-y-3">

                                    {/* مجموع کالاها */}
                                    <div className="flex justify-between items-center text-sm">

                                        <span className="text-gray-600 dark:text-gray-300">
                                            مجموع قیمت کالاها:
                                        </span>

                                        <span className="font-medium">
                                            {numberWithCommas(subtotal)} تومان
                                        </span>

                                    </div>

                                    {/* تخفیف */}
                                    {discountTotal > 0 && (
                                        <div className="flex justify-between items-center text-sm text-green-600">

                                            <span>
                                                کد تخفیف لحاظ شده:
                                            </span>

                                            <span className="font-medium">
                                                - {numberWithCommas(discountTotal)} تومان
                                            </span>

                                        </div>
                                    )}

                                    {/* هزینه ارسال */}
                                    <div className="flex justify-between items-center text-sm">

                                        <span className="text-gray-600 dark:text-gray-300">
                                            هزینه ارسال:
                                        </span>

                                        <span className="font-medium">
                                            {shippingCost > 0
                                                ? `${numberWithCommas(shippingCost)} تومان`
                                                : 'رایگان'}
                                        </span>

                                    </div>

                                    {/* جمع نهایی */}
                                    <div className="pt-3 border-t border-gray-300 dark:border-gray-600">

                                        <div className="flex justify-between items-center font-bold text-lg">

                                            <span>
                                                جمع کل سفارش:
                                            </span>

                                            <span className="text-primary-color">
                                                {numberWithCommas(finalTotal)} تومان
                                            </span>

                                        </div>

                                    </div>

                                </div>

                            </div>
                        </div>
                    </div>
                </ModalBody>

                <ModalFooter>
                    <div className="flex gap-2">
                        <TwinButton
                            acceptTitle="ذخیره تغییرات"
                            acceptHandle={handleSaveChanges}
                            rejectHandle={handleCancel}
                            rejectTitle="انصراف"
                            isCompact={true}
                            isLoading={isLoading}
                        />
                    </div>
                </ModalFooter>
            </Modal>
        </>
    )
}